// A page that is a workspace rather than a document. The template editor is
// one: a canvas with panels either side wants every pixel under the top bar,
// and none of the page padding or the reading width the other screens keep.
// It says so with `usePageBleed`, and the shell drops the padding, lets the
// workspace run the full width, folds the side navigation behind the menu
// button (as on a phone), and fixes the column to the window's height so the
// panels scroll on their own instead of the page scrolling under them.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface BleedSlot {
  bleed: boolean;
  setBleed: (bleed: boolean) => void;
}

const BleedContext = createContext<BleedSlot | null>(null);

export function BleedProvider({ children }: { children: ReactNode }) {
  const [bleed, setBleed] = useState(false);
  const value = useMemo(() => ({ bleed, setBleed }), [bleed]);
  return <BleedContext.Provider value={value}>{children}</BleedContext.Provider>;
}

export function useBleed(): boolean {
  return useContext(BleedContext)?.bleed ?? false;
}

/** Take the whole workspace while this page is on screen. */
export function usePageBleed(): void {
  const setBleed = useContext(BleedContext)?.setBleed;
  useEffect(() => {
    if (!setBleed) return undefined;
    setBleed(true);
    return () => setBleed(false);
  }, [setBleed]);
}
